import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { maxLength, required, composeValidators } from '../../util/validators';
import { Button, Form, FieldTextInput, FieldSelect } from '../../components';
import ReCAPTCHA from 'react-google-recaptcha';

import css from './ContactUsForm.module.css';
import * as validators from '../../util/validators';
import { useState, useEffect } from 'react';
// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};


const ContactUsForm = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        autoFocus,
        className,
        disabled,
        ready,
        formId = 'ContactUsForm',
        form: formApi,
        handleSubmit,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        inProgress,
        messageSent,
        sendMessageError,
        showSuccess,
      } = formRenderProps;

      const intl = useIntl();

      const [captchaSize, setCaptchaSize] = useState("compact");

      const nameLabel = intl.formatMessage({ id: 'ContactUsForm.nameLabel' });
      const namePlaceholder = intl.formatMessage({ id: 'ContactUsForm.namePlaceholder' });
      const emailLabel = intl.formatMessage({ id: 'ContactUsForm.emailLabel' });
      const emailPlaceholder = intl.formatMessage({ id: 'ContactUsForm.emailPlaceholder' });
      const topicLabel = intl.formatMessage({ id: 'ContactUsForm.topicLabel' });
      const topicPlaceholder = intl.formatMessage({ id: 'ContactUsForm.topicPlaceholder' });
      const messageLabel = intl.formatMessage({ id: 'ContactUsForm.messageLabel' });
      const messagePlaceholder = intl.formatMessage({ id: 'ContactUsForm.messagePlaceholder' });
      const submitText = intl.formatMessage({ id: 'ContactUsForm.submit' });


      const requireText = intl.formatMessage({ id: 'ContactUsForm.required' });
      const emailInvalid = intl.formatMessage({ id: 'ContactUsForm.emailInvalid' });

      const required = validators.required(requireText);
      const emailRequired = validators.composeValidators(required, validators.emailFormatValid(emailInvalid));

      const topicOptions = [
        { value: 'general', label: intl.formatMessage({ id: 'ContactUsForm.topic.general' }) },
        { value: 'buying', label: intl.formatMessage({ id: 'ContactUsForm.topic.buying' }) },
        { value: 'selling', label: intl.formatMessage({ id: 'ContactUsForm.topic.selling' }) },
        { value: 'account', label: intl.formatMessage({ id: 'ContactUsForm.topic.account' }) },
        { value: 'other', label: intl.formatMessage({ id: 'ContactUsForm.topic.other' }) },
      ];
      const classes = classNames(css.root, className);

      const submitDisabled = pristine || invalid || inProgress;


      useEffect(() => {
        const handleResize = () => {
          if (window.innerWidth <= 600) {
            setCaptchaSize("compact");   // <= 600px → normal
          } else {
            setCaptchaSize("normal");  // > 600px → compact
          }
        };

        handleResize(); // run once
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);

      return (

        <Form onSubmit={handleSubmit}>
          <ErrorMessage fetchErrors={fetchErrors} />
          <div className={css.inputRow}>
            <FieldTextInput
              id="name"
              name="name"
              type="text"
              label={nameLabel}
              placeholder={namePlaceholder}
              validate={required}
              disabled={inProgress}
            />
            <FieldTextInput
              id="email"
              name="email"
              type="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={emailRequired}
              disabled={inProgress}
            />
          </div>

          <div className={css.formRow}>
            <FieldSelect
              id="topic"
              name="topic"
              label={topicLabel}
              validate={required}
              disabled={inProgress}
            >
              <option disabled value="">
                {topicPlaceholder}
              </option>
              {topicOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FieldSelect>
          </div>

          <div className={css.formRow}>
            <FieldTextInput
              id="message"
              name="message"
              type="textarea"
              label={messageLabel}
              placeholder={messagePlaceholder}
              validate={required}
              disabled={inProgress}
            />
          </div>

          <Field
            name="captchaToken"
            component="input"
            type="hidden"
            validate={required}
          />

          <div className={classNames(css.formRow, css.rechapta)}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => formApi.change('captchaToken', token)}
              onExpired={() => formApi.change('captchaToken', null)}
              size={captchaSize}
            />
          </div>

          {showSuccess && (
            <div className={css.successMessage}>
              <FormattedMessage id="ContactUsForm.successMessage" />
            </div>
          )}

          {sendMessageError && (
            <div className={css.errorMessage}>
              <FormattedMessage id="ContactUsForm.errorMessage" />
            </div>
          )}
          <div className={css.submitRow}>
            <Button type="submit" disabled={submitDisabled} inProgress={inProgress}>
              {submitText}
            </Button>
            <p className={css.responseNote}>
              <FormattedMessage id="ContactUsForm.responseNote" /> {' '}
              <span className={css.highlight}>
                <FormattedMessage id='ContactUsForm.responseDays' /></span> {' '}
              <FormattedMessage id='ContactUsForm.responseDaysTextOnly' />
            </p>
          </div>
        </Form>

      );
    }}
  />
);

export default ContactUsForm;
